import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import AgeGate from "./components/AgeGate";
import Header from "./components/Header";
import Hero from "./components/Hero";
import ProductGrid from "./components/ProductGrid";
import PromoSection from "./components/PromoSection";
import NewsletterPopup from "./components/NewsletterPopup";
import Footer from "./components/Footer";
import { strains } from "./data/mockData";

const Home = () => {
  const [showAgeGate, setShowAgeGate] = useState(false);
  const [showNewsletter, setShowNewsletter] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [filteredProducts, setFilteredProducts] = useState(strains);

  useEffect(() => {
    // Check if age has been verified
    const ageVerified = localStorage.getItem('ageVerified');
    if (!ageVerified) {
      setShowAgeGate(true);
    } else {
      // Show newsletter popup after 15 seconds if not shown recently
      const newsletterShown = localStorage.getItem('newsletterShown');
      const lastShown = localStorage.getItem('newsletterLastShown');
      const now = Date.now();
      const oneDay = 24 * 60 * 60 * 1000;
      
      if (!newsletterShown || (lastShown && now - parseInt(lastShown) > oneDay)) {
        setTimeout(() => {
          setShowNewsletter(true);
        }, 15000);
      }
    }
  }, []);

  useEffect(() => {
    if (selectedCategory === 'all') {
      setFilteredProducts(strains);
    } else {
      setFilteredProducts(strains.filter(strain => strain.category === selectedCategory));
    }
  }, [selectedCategory]);

  const handleAgeGateClose = () => {
    setShowAgeGate(false);
  };

  const handleMenuClick = (categorySlug) => {
    setSelectedCategory(categorySlug);
    // Scroll to products section
    document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleShopNow = () => {
    document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleNewsletterClose = () => {
    setShowNewsletter(false);
    localStorage.setItem('newsletterShown', 'true');
    localStorage.setItem('newsletterLastShown', Date.now().toString());
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <AgeGate isOpen={showAgeGate} onClose={handleAgeGateClose} />
      
      <Header onMenuClick={handleMenuClick} />
      
      <main>
        <Hero onShopNow={handleShopNow} />
        
        {/* Featured Strains Section */}
        <section className="py-16 bg-gradient-to-b from-black to-gray-900">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-white mb-4">
                Featured Strains
              </h2>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                Discover our hand-picked selection of premium cannabis strains, 
                each one carefully curated for exceptional quality and effects.
              </p>
            </div>
            
            <ProductGrid 
              products={strains.filter(strain => strain.featured)} 
              selectedCategory="featured"
            />
          </div>
        </section>
        
        <PromoSection />
        
        {/* Full Menu Section */}
        <section id="products" className="py-16 bg-gray-900">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-white mb-4">
                Premium Cannabis Menu
              </h2>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                Browse our complete collection of top-shelf strains, 
                each one lab-tested and quality guaranteed.
              </p>
            </div>
            
            <ProductGrid 
              products={filteredProducts} 
              selectedCategory={selectedCategory}
            />
          </div>
        </section>

        {/* Newsletter Section */}
        <section className="py-16 bg-gradient-to-r from-green-900 to-black">
          <div className="container mx-auto px-4 text-center">
            <h3 className="text-3xl font-bold text-white mb-4">
              Stay Connected
            </h3>
            <p className="text-gray-300 mb-6 max-w-md mx-auto">
              Get exclusive deals, new strain alerts, and insider access to Big Dog Kush drops.
            </p>
            
            {/* Contact Methods */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
              <button 
                onClick={() => setShowNewsletter(true)}
                className="bg-green-600 hover:bg-green-700 text-white font-semibold px-8 py-3 rounded-xl transition-all duration-300"
              >
                Join Our Newsletter
              </button>
              
              <div className="text-gray-300">or</div>
              
              <button 
                onClick={() => window.location.href = 'sms:+19455457553?body=' + encodeURIComponent('Hi! I want to stay updated on Big Dog Kush deals and new strains. Please add me to your list!')}
                className="bg-gray-800 hover:bg-gray-700 text-green-400 font-semibold px-8 py-3 rounded-xl transition-all duration-300 border border-green-600"
              >
                Text (945) 545-7553
              </button>
            </div>
            
            <p className="text-gray-400 text-sm">
              Fast updates • Exclusive deals • New strain alerts
            </p>
          </div>
        </section>
      </main>
      
      <Footer />
      
      <NewsletterPopup 
        isOpen={showNewsletter} 
        onClose={handleNewsletterClose} 
      />
      
      <Toaster theme="dark" />
    </div>
  );
};

function App() {
  return (
    <div className="App bg-black">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;